import { Injectable, NgZone, Type } from "@angular/core";
import { VngDialogService, VngDialogRef, VngDialogConfig } from "@vengreso/angular-components";
import { NavigationStart, Router } from "@angular/router";
import { Observable } from "rxjs";
import { filter, map } from "rxjs/operators";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from "src/app/components/dialogs/confirm-dialog/confirm-dialog.component";

/**
 * Named dialog widths. Use these instead of repeating raw pixel strings at
 * call sites ("330px"/"400px"/"800px"/… were previously duplicated ~100×).
 */
export const DIALOG_WIDTHS = {
  /** Compact confirms and small forms. */
  sm: "400px",
  /** Standard forms. */
  md: "500px",
  /** Editors and rich dialogs. */
  lg: "800px",
  /** Share/invite dialogs. */
  xl: "859px",
} as const;

/**
 * Data bag passed to `openDialogComponent`. Anything you put here is
 * injectable in the dialog via `@Inject(VNG_DIALOG_DATA)`; the keys below
 * additionally configure the dialog itself.
 */
export interface AppDialogData {
  /** Applied as the dialog's panelClass (also queryable via `hasOpenDialogWithClass`). */
  class?: string;
  backdropClass?: string;
  /**
   * Legacy escape hatch: the opening component passes itself so the dialog
   * can call back into it. If the host exposes `handleCloseDialog()`, it is
   * invoked after the dialog closes. Prefer subscribing to the returned
   * ref's `afterClosed()` (or `DialogService.confirm()`) in new code.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  self?: any;
  [key: string]: unknown;
}

/** Options for {@link DialogService.confirm}. */
export interface ConfirmDialogOptions {
  /** Dialog width. Defaults to {@link DIALOG_WIDTHS.sm}. */
  width?: string;
  /** When true, backdrop/Escape do not close — only the buttons. */
  disableClose?: boolean;
}

/**
 * Extra {@link DialogService.openDialogComponent} knobs that have no positional
 * parameter. Keep this an options bag — the four positional params below are
 * load-bearing at ~100 existing call sites.
 */
export interface OpenDialogOptions {
  /**
   * CSS padding for the dialog panel. VngDialogService writes panel padding as
   * an INLINE style (`[style.padding]="config.padding ?? '1rem'"` in
   * vng-dialog-container.component.ts), which beats any stylesheet rule — so a
   * panelClass alone cannot flush a dialog's content to its edges. Pass "0"
   * for edge-to-edge dialogs (e.g. the video player).
   */
  padding?: string;
}

@Injectable()
export class DialogService {
  // Tracks dialogs opened via openDialogComponent (keyed by their panelClass
  // "class" data field) so callers can ask "is a dialog of kind X already
  // open?" without VngDialogService exposing a public openDialogs list (the
  // way MatDialog did) — see welcome.component.ts's "welcome-dialog" guard.
  // `config` (issue #520) is the SAME object reference handed to
  // VngDialogService.open() — see setDisableClose() below for why that
  // matters.
  private readonly openDialogsRegistry: { ref: VngDialogRef<any>; data: any; config: VngDialogConfig }[] = [];

  constructor(
    private dialog: VngDialogService,
    private zone: NgZone,
    private router: Router
  ) {
    // closeOnNavigation is accepted by VngDialogConfig but is a no-op in
    // VngDialogService (verified in lib source) — reimplement here to
    // preserve today's behavior, in which dialogs close on route change.
    //
    // The closeAll() call is deferred a microtask because Angular's Router
    // fires NavigationStart SYNCHRONOUSLY from within router.navigate()'s
    // call stack (NavigationTransitions.setupNavigations drives a
    // switchMap off a BehaviorSubject — no promise/timer boundary before
    // the NavigationStart emission). That matters here because a very
    // common pattern is "a dialog's afterClosed() handler triggers
    // navigation" (e.g. verify-email.component.ts's openSuccessModal(),
    // or CanDeactivateFormGuard's "Leave" flow): VngDialogRef.close()
    // (angular-components src/lib/dialog/vng-dialog.types.ts) emits
    // `_afterClosed$.next()` to all subscribers *before* calling
    // `_closeFn()`, which is what removes the dialog from
    // VngDialogService's `openDialogs` list. So if we called closeAll()
    // synchronously here, a dialog whose own afterClosed() subscriber
    // triggered this NavigationStart would still be present in
    // `openDialogs`, and closeAll() would re-enter close() on that SAME
    // ref while its Subject is still open — re-firing every afterClosed()
    // subscriber (including the one that just called navigate()) and
    // recursing synchronously into router.navigate() -> NavigationStart ->
    // closeAll() again. That loop starves the original navigation (each
    // new navigate() cancels/supersedes the last), so the app never
    // actually leaves the current route. Deferring one microtask lets the
    // triggering close() finish (running _closeFn()) first, so by the
    // time this runs the dialog is already gone from openDialogs and
    // closeAll() is a no-op for it. See
    // src/app/session/verify-email/verify-email.component.spec.ts for a
    // reproduction, and C3-1-fix commit for the full writeup — this
    // pattern (dialog close -> navigate) will recur in any dialog-heavy
    // vertical, so keep this deferral if this subscription is ever moved.
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      queueMicrotask(() => this.dialog.closeAll());
    });
  }

  /**
   * Opens `component` as a dialog with the app-wide defaults (max size caps,
   * zone entry, close-on-navigation, panel class from `data.class`).
   * `R` is the type emitted by `afterClosed()` — pass it explicitly in new
   * code (it defaults to `any` only for the pre-existing call sites).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  openDialogComponent<R = any>(
    component: Type<unknown>,
    data: AppDialogData,
    width: string = DIALOG_WIDTHS.sm,
    disableClose: boolean = false,
    minWidth?: string,
    options: OpenDialogOptions = {}
  ): VngDialogRef<unknown, R> {
    const safeData = data || {};
    // Built OUTSIDE the object literal handed to VngDialogService.open() so
    // this same reference can be kept in the registry (see setDisableClose())
    // — the config isn't cloned internally, so mutating THIS object after
    // open() is how a dialog's disableClose can be live-toggled later.
    const config: VngDialogConfig = {
      data: safeData,
      minWidth,
      width,
      maxWidth: "calc(100vw - 40px)",
      maxHeight: "calc(100vh - 40px)",
      panelClass: safeData.class,
      disableClose,
      backdropClass: safeData.backdropClass,
      padding: options.padding,
    };
    let dialogRef: VngDialogRef<unknown, R>;
    this.zone.run(() => {
      dialogRef = this.dialog.open(component, config);
      return dialogRef;
    });

    const entry = { ref: dialogRef, data: safeData, config };
    this.openDialogsRegistry.push(entry);
    dialogRef.afterClosed().subscribe(() => {
      const idx = this.openDialogsRegistry.indexOf(entry);
      if (idx > -1) this.openDialogsRegistry.splice(idx, 1);
    });

    if (safeData.self?.handleCloseDialog) {
      dialogRef.afterClosed().subscribe(() => {
        safeData.self.handleCloseDialog();
      });
    }
    return dialogRef;
  }

  /**
   * Opens the shared yes/no {@link ConfirmDialogComponent} and emits exactly
   * once: `true` when the user confirmed, `false` on cancel/X/backdrop/Escape.
   *
   * ```ts
   * this.dialog
   *   .confirm({ title: "Delete this FlyCut?", variant: "danger", confirmLabel: "Delete" })
   *   .subscribe((confirmed) => confirmed && this.delete(flycut));
   * ```
   */
  confirm(data: ConfirmDialogData, options: ConfirmDialogOptions = {}): Observable<boolean> {
    const { width = DIALOG_WIDTHS.sm, disableClose = false } = options;
    return this.openDialogComponent<boolean>(ConfirmDialogComponent, { ...data }, width, disableClose)
      .afterClosed()
      .pipe(map((confirmed) => confirmed === true));
  }

  /**
   * True if a dialog opened via openDialogComponent with this panelClass
   * ("class" data field) is currently open. Replaces the pre-migration
   * `matDialog.openDialogs` scan (MatDialog exposed its open-dialogs list
   * publicly; VngDialogService does not).
   */
  hasOpenDialogWithClass(className: string): boolean {
    return this.openDialogsRegistry.some((entry) => entry.data?.class === className);
  }

  /**
   * Closes any currently-open dialog(s) opened via openDialogComponent with
   * this panelClass ("class" data field). Companion to
   * hasOpenDialogWithClass — for callers that need to actively dismiss a
   * dialog from OUTSIDE it, e.g. issue #518 (G-10): the "Download the
   * extension" welcome nag must not keep demanding an install the app has
   * already detected, so the detection point closes it directly rather than
   * waiting for the nag to notice on its own. Iterates a snapshot (not the
   * live registry array) since each close() synchronously triggers the
   * registry's own afterClosed()-driven splice.
   */
  closeDialogWithClass(className: string): void {
    this.openDialogsRegistry
      .filter((entry) => entry.data?.class === className)
      .forEach((entry) => entry.ref.close());
  }

  /**
   * Issue #520: live-toggles a dialog's disableClose (backdrop-click/Escape
   * dismissal) AFTER it's already open — for guardrails that must block
   * dismissal until some in-dialog condition is met and un-block it the
   * moment that condition flips, without closing/reopening the dialog.
   *
   * VngDialogContainerComponent reads `config.disableClose` fresh on every
   * backdrop click / Escape keydown (it's never cached at open time), and
   * VngDialogService.open() hands the container the EXACT SAME config object
   * this registry already holds (openDialogComponent() never clones it) — so
   * mutating that object here is enough; VngDialogRef itself exposes no API
   * for this. No-ops if `ref` isn't a currently-open dialog opened via
   * openDialogComponent (e.g. it already closed).
   */
  setDisableClose(ref: VngDialogRef<unknown>, disableClose: boolean): void {
    const entry = this.openDialogsRegistry.find((e) => e.ref === ref);
    if (entry) {
      entry.config.disableClose = disableClose;
    }
  }

  closeDialog() {
    this.dialog.closeAll();
  }
}
